/**
 * Secure Access → MDM Integrations (VSNP-1169, docs/17 §8)
 *
 * A tenant admin's integrations with their own MDM (Intune / Jamf) or a
 * generic push feed. Tenant-scoped (TenantGuard); the API scopes every call by
 * tenant, so an admin only ever sees their own integrations. Vendor secrets are
 * write-only (never echoed); the generic push token is shown exactly once.
 */

import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Trash2, Pencil, PlugZap, Play, ShieldCheck } from 'lucide-react';
import DataTable, { type Column } from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Modal from '../../components/common/Modal';
import AccessFormField from '../../components/access/AccessFormField';
import ZtnaChip from '../../components/access/ZtnaChip';
import { useToast } from '../../components/common/ToastContainer';
import accessService, {
  type MdmIntegration,
  type MdmIntegrationData,
  type MdmMatchKey,
  type MdmProviderId,
  type MdmProviderMeta,
  type MdmSyncResult,
} from '../../services/access.service';

/** Labels/placeholders for provider config keys (per docs/17 §2). */
const CONFIG_FIELDS: Record<string, { label: string; placeholder: string; hint?: string; mono?: boolean }> = {
  tenantId: { label: 'Entra tenant ID', placeholder: '72f988bf-86f1-41af-91ab-2d7cd011db47', hint: 'The Microsoft Entra tenant GUID of the app registration.', mono: true },
  clientId: { label: 'Client ID', placeholder: 'app registration / API client id', mono: true },
  baseUrl: { label: 'Jamf Pro base URL', placeholder: 'https://acme.jamfcloud.com', mono: true },
};

const MATCH_KEY_LABEL: Record<MdmMatchKey, string> = {
  email_hostname: 'Owner email + hostname (recommended)',
  hostname: 'Hostname only',
  mdm_device_id: 'MDM device id (explicit binding only)',
};

function fmtSync(i: MdmIntegration): string {
  if (!i.last_sync_at) return 'never';
  const when = new Date(i.last_sync_at).toLocaleString();
  if (i.last_sync_status === 'error') return `error · ${when}`;
  const seen = i.last_sync_devices ?? 0;
  const matched = i.last_sync_matched ?? 0;
  return `${matched}/${seen} matched · ${when}`;
}

const MdmIntegrations = () => {
  const toast = useToast();
  const [items, setItems] = useState<MdmIntegration[]>([]);
  const [providers, setProviders] = useState<MdmProviderMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorTarget, setEditorTarget] = useState<MdmIntegration | null | 'new'>(null);
  const [deleteTarget, setDeleteTarget] = useState<MdmIntegration | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [list, meta] = await Promise.allSettled([accessService.listMdmIntegrations(), accessService.listMdmProviders()]);
      setItems(list.status === 'fulfilled' ? list.value ?? [] : []);
      setProviders(meta.status === 'fulfilled' ? meta.value ?? [] : []);
      if (list.status === 'rejected') {
        setError(list.reason instanceof Error ? list.reason.message : 'Failed to load MDM integrations');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const handleTest = async (i: MdmIntegration) => {
    setBusyId(i.id);
    try {
      const r = await accessService.testMdmIntegration(i.id);
      if (r.ok) {
        toast.success('Connection OK', r.error ?? `${r.devicesSeen} device(s) visible${r.sample.length ? `: ${r.sample.join(', ')}` : ''}`);
      } else {
        toast.error('Connection failed', r.error ?? 'The MDM rejected the credentials');
      }
    } catch (err) {
      toast.error('Test failed', err instanceof Error ? err.message : 'Request failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleSync = async (i: MdmIntegration) => {
    setBusyId(i.id);
    try {
      const r: MdmSyncResult = await accessService.syncMdmIntegration(i.id);
      toast.success(
        'Sync complete',
        `${r.matched}/${r.seen} matched, ${r.changed} device(s) updated${r.recompiled ? ', bundle republished' : ''}` +
          (r.unmatched.length ? ` — unmatched: ${r.unmatched.slice(0, 3).join(', ')}${r.unmatched.length > 3 ? '…' : ''}` : ''),
      );
      await fetchAll();
    } catch (err) {
      toast.error('Sync failed', err instanceof Error ? err.message : 'Request failed');
      await fetchAll();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await accessService.deleteMdmIntegration(deleteTarget.id);
      toast.success('Integration deleted', deleteTarget.display_name);
      setDeleteTarget(null);
      await fetchAll();
    } catch (err) {
      toast.error('Delete failed', err instanceof Error ? err.message : 'Request failed');
    } finally {
      setDeleting(false);
    }
  };

  const providerLabel = (id: MdmProviderId) => providers.find((p) => p.id === id)?.label ?? id;

  const columns: Column<MdmIntegration>[] = [
    {
      key: 'display_name',
      label: 'Integration',
      sortable: true,
      render: (i) => (
        <div>
          <div className="font-medium text-gray-900">{i.display_name}</div>
          <div className="text-xs text-gray-500">{providerLabel(i.provider)}{i.provider === 'generic' ? ' · push' : ' · pull'}</div>
        </div>
      ),
    },
    {
      key: 'enabled',
      label: 'Status',
      render: (i) => <StatusBadge status={i.enabled ? 'active' : 'inactive'} />,
    },
    {
      key: 'last_sync',
      label: 'Last sync',
      render: (i) => (
        <div className="text-sm">
          <div className={i.last_sync_status === 'error' ? 'text-danger-600' : 'text-gray-700'}>{fmtSync(i)}</div>
          {i.last_sync_error && <div className="text-xs text-danger-600 truncate max-w-xs" title={i.last_sync_error}>{i.last_sync_error}</div>}
        </div>
      ),
    },
    {
      key: 'match_key',
      label: 'Correlation',
      render: (i) => <span className="text-xs text-gray-600">{MATCH_KEY_LABEL[i.match_key] ?? i.match_key}</span>,
    },
    {
      key: 'actions',
      label: '',
      render: (i) => (
        <div className="flex items-center justify-end gap-1">
          <button type="button" className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-primary-600 disabled:opacity-40" title="Test connection" disabled={busyId === i.id} onClick={() => void handleTest(i)}>
            <PlugZap className="w-4 h-4" />
          </button>
          {i.provider !== 'generic' && (
            <button type="button" className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-primary-600 disabled:opacity-40" title="Sync now" disabled={busyId === i.id} onClick={() => void handleSync(i)}>
              <Play className="w-4 h-4" />
            </button>
          )}
          <button type="button" className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-primary-600" title="Edit" onClick={() => setEditorTarget(i)}>
            <Pencil className="w-4 h-4" />
          </button>
          <button type="button" className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-danger-600" title="Delete" onClick={() => setDeleteTarget(i)}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">MDM Integrations</h1>
            <ZtnaChip />
          </div>
          <p className="text-gray-600 mt-1">
            Connect this tenant's MDM so device compliance is vouched by your MDM (<span className="font-mono">source=mdm</span>) and overrides self-reported posture.
          </p>
        </div>
        <div className="flex space-x-2">
          <button type="button" className="btn-secondary" onClick={() => void fetchAll()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 inline ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button type="button" className="btn-primary" onClick={() => setEditorTarget('new')}>
            <Plus className="w-4 h-4 mr-2 inline" />
            Add Integration
          </button>
        </div>
      </div>

      {error && <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">{error}</div>}

      <div className="card">
        <DataTable
          columns={columns}
          data={items}
          loading={loading}
          searchPlaceholder="Search integrations…"
          emptyMessage="No MDM integration yet — posture is self-reported until you add one"
        />
      </div>

      <div className="card text-sm text-gray-600 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
        <div>
          Devices reported compliant by the MDM get <span className="font-mono">source=mdm</span> posture that rides the signed policy bundle;
          require it on sensitive policies with <span className="font-mono">Posture source in {'{attested, mdm}'}</span>. Devices the MDM stops
          reporting lose the vouch after the stale window. MDM is optional — nothing here is required for self-reported posture to work.
        </div>
      </div>

      {editorTarget && (
        <IntegrationEditor
          key={editorTarget === 'new' ? 'new' : editorTarget.id}
          target={editorTarget}
          providers={providers}
          onClose={() => setEditorTarget(null)}
          onSaved={async () => {
            setEditorTarget(null);
            await fetchAll();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete MDM integration"
        message={`Delete '${deleteTarget?.display_name}'? Devices keep their current vouched posture until it goes stale or is recompiled away.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

/** Keyed by target so it remounts (and re-seeds) per open — no reset-in-effect. */
const IntegrationEditor = ({
  target,
  providers,
  onClose,
  onSaved,
}: {
  target: MdmIntegration | 'new';
  providers: MdmProviderMeta[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) => {
  const toast = useToast();
  const isNew = target === 'new';
  const editing = isNew ? null : target;

  const [provider, setProvider] = useState<MdmProviderId>(editing?.provider ?? 'intune');
  const [displayName, setDisplayName] = useState(editing?.display_name ?? '');
  const [config, setConfig] = useState<Record<string, string>>(editing?.config ?? {});
  const [secret, setSecret] = useState('');
  const [matchKey, setMatchKey] = useState<MdmMatchKey>(editing?.match_key ?? 'email_hostname');
  const [pollIntervalMin, setPollIntervalMin] = useState(editing?.poll_interval_min ?? 15);
  const [staleAfterHours, setStaleAfterHours] = useState(editing?.stale_after_hours ?? 24);
  const [enabled, setEnabled] = useState(editing?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<MdmIntegration | null>(null);

  const meta = providers.find((p) => p.id === provider);
  const ingestUrl = `${window.location.origin}/api/v2/access/mdm/ingest`;

  const submit = async () => {
    try {
      setSaving(true);
      if (!displayName.trim()) {
        toast.error('Missing fields', 'A display name is required');
        return;
      }
      if (isNew) {
        const data: MdmIntegrationData = {
          provider,
          displayName: displayName.trim(),
          config,
          secret: secret || undefined,
          matchKey,
          pollIntervalMin,
          staleAfterHours,
          enabled,
        };
        const c = await accessService.createMdmIntegration(data);
        toast.success('Integration created', c.display_name);
        if (c.provider === 'generic' && c.pushToken) {
          setCreated(c); // show the one-time token
        } else {
          await onSaved();
        }
      } else if (editing) {
        await accessService.updateMdmIntegration(editing.id, {
          displayName: displayName.trim(),
          config,
          secret: secret || undefined,
          matchKey,
          pollIntervalMin,
          staleAfterHours,
          enabled,
        });
        toast.success('Integration updated', secret ? 'Secret rotated' : displayName);
        await onSaved();
      }
    } catch (err) {
      toast.error(isNew ? 'Create failed' : 'Update failed', err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={() => (created ? void onSaved() : onClose())}
      title={isNew ? 'Add MDM Integration' : `Edit — ${editing?.display_name ?? ''}`}
      size="lg"
      footer={
        created ? (
          <div className="flex justify-end">
            <button type="button" className="btn-primary" onClick={() => void onSaved()}>Done</button>
          </div>
        ) : (
          <div className="flex justify-end space-x-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" className="btn-primary" onClick={() => void submit()} disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create Integration' : 'Save'}
            </button>
          </div>
        )
      }
    >
      {created ? (
        <div className="space-y-3 text-sm">
          <p className="text-gray-700">
            <strong>{created.display_name}</strong> is ready. This push token is shown <strong>once</strong> — store it in your MDM/script now:
          </p>
          <code className="block rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs break-all">{created.pushToken}</code>
          <p className="text-gray-700">POST device compliance in the posture vocabulary to:</p>
          <code className="block rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs break-all">{ingestUrl}</code>
          <pre className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] overflow-x-auto">{`curl -X POST ${ingestUrl} \\
  -H "Authorization: Bearer ${created.pushToken}" \\
  -H "Content-Type: application/json" \\
  -d '{"devices":[{"hostname":"alice-laptop","userEmail":"alice@example.com",
        "facts":{"disk_encryption":"pass","device_integrity":"pass","edr":"pass"}}]}'`}</pre>
          <p className="text-xs text-gray-500">Records are correlated only to devices in this tenant; anything that doesn't match is reported back as unmatched.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {isNew && (
            <AccessFormField label="Provider" htmlFor="mdm-provider" required>
              <select
                id="mdm-provider"
                className="input-base w-full"
                value={provider}
                onChange={(e) => {
                  setProvider(e.target.value as MdmProviderId);
                  setConfig({});
                  setSecret('');
                }}
              >
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </AccessFormField>
          )}
          <AccessFormField label="Display name" htmlFor="mdm-name" required>
            <input id="mdm-name" className="input-base w-full" placeholder="Corp Intune" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </AccessFormField>

          {(meta?.requiredConfig ?? []).map((k) => {
            const f = CONFIG_FIELDS[k] ?? { label: k, placeholder: '' };
            return (
              <AccessFormField key={k} label={f.label} htmlFor={`mdm-cfg-${k}`} required hint={f.hint}>
                <input
                  id={`mdm-cfg-${k}`}
                  className={`input-base w-full ${f.mono ? 'font-mono' : ''}`}
                  placeholder={f.placeholder}
                  value={config[k] ?? ''}
                  onChange={(e) => setConfig((c) => ({ ...c, [k]: e.target.value }))}
                />
              </AccessFormField>
            );
          })}

          {meta?.needsSecret && (
            <AccessFormField
              label={isNew ? 'Client secret' : 'New client secret'}
              htmlFor="mdm-secret"
              required={isNew}
              hint={isNew ? 'Encrypted at rest; never shown again. Use a READ-ONLY API credential.' : 'Leave blank to keep the current secret.'}
            >
              <input id="mdm-secret" type="password" autoComplete="new-password" className="input-base w-full font-mono" value={secret} onChange={(e) => setSecret(e.target.value)} />
            </AccessFormField>
          )}

          {provider === 'generic' && isNew && (
            <p className="text-xs text-gray-500">
              No vendor credentials — your MDM (or a script) pushes compliance to us with a token generated on create.
            </p>
          )}

          <AccessFormField label="Device correlation" htmlFor="mdm-match" hint="How an MDM record is matched to an enrolled device. Matching never crosses tenants.">
            <select id="mdm-match" className="input-base w-full" value={matchKey} onChange={(e) => setMatchKey(e.target.value as MdmMatchKey)}>
              {(Object.keys(MATCH_KEY_LABEL) as MdmMatchKey[]).map((k) => (
                <option key={k} value={k}>{MATCH_KEY_LABEL[k]}</option>
              ))}
            </select>
          </AccessFormField>

          <div className="grid grid-cols-2 gap-4">
            {provider !== 'generic' && (
              <AccessFormField label="Poll interval (minutes)" htmlFor="mdm-poll">
                <input id="mdm-poll" type="number" min={5} max={1440} className="input-base w-full" value={pollIntervalMin} onChange={(e) => setPollIntervalMin(Number(e.target.value))} />
              </AccessFormField>
            )}
            <AccessFormField label="Stale after (hours)" htmlFor="mdm-stale" hint="A device the MDM stops reporting loses its vouch after this.">
              <input id="mdm-stale" type="number" min={1} max={720} className="input-base w-full" value={staleAfterHours} onChange={(e) => setStaleAfterHours(Number(e.target.value))} />
            </AccessFormField>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Enabled
          </label>
        </div>
      )}
    </Modal>
  );
};

export default MdmIntegrations;
